/* eslint-disable no-param-reassign */
import { InitialConnectAppFormValues } from 'src/components/ExtensionPage/ConnectManualApp';
import { URLValidationRegex } from 'src/constants';
import { EmbedErrors } from 'src/constants/errorConsts/errorCodesConsts';
import { ExtensionItem, ExtensionItemFields } from 'src/store/dashboard/types';

export enum EmbedChannelTypes {
  Client = 'client',
  Company = 'company',
}

const autoSizeElementTypes = ['div', 'iframe'];

export const createIFrame = (embedURL: string) =>
  `<iframe src="${embedURL}"></iframe>`;

interface CreateEmbedBodyInput {
  embedCode: string;
  autoSize: boolean;
  customAppParams?: {
    // query params to add to the embed url
    companyId: string;
    clientId?: string; // client id is optional for company channels when viewing as internal user
  };
}

export const DefaultChannelTypes = [
  {
    label: 'Individual client channels',
    value: EmbedChannelTypes.Client,
  },

  {
    label: 'Company channels',
    value: EmbedChannelTypes.Company,
  },
];

export const createEmbedBody = ({
  embedCode,
  autoSize,
  // authEmbedData,
  customAppParams,
}: CreateEmbedBodyInput) => {
  const urlRegex = new RegExp(URLValidationRegex);
  let embedSnippet = embedCode;
  if (embedCode.match(urlRegex)) {
    let url: URL;
    try {
      url = new URL(embedCode);
    } catch (ex) {
      console.error('Embed code is not a valid url', embedCode, ex);
      return EmbedErrors.errorRenderExtension;
    }

    // when customAppParams is provided append the params to the url
    if (customAppParams) {
      Object.keys(customAppParams).forEach(key => {
        url.searchParams.set(key, customAppParams[key]);
      });
    }
    // get full url and create Iframe snippet
    embedSnippet = createIFrame(url.toString());
  }

  const parser = new DOMParser();
  const htmlDoc = parser.parseFromString(embedSnippet, 'text/html');

  // override iframes that have `no` scrolling behavior
  // the explicit check for scrolling=no is recommended since this property
  // is deprecated and we dont want to set the value if it does not need to be
  Array.from(htmlDoc.body.children).forEach(elem => {
    const elemTagName = elem.tagName.toLowerCase();
    if (elemTagName === 'iframe') {
      const iframeScrollingAttributeValue = elem.getAttribute('scrolling');
      if (iframeScrollingAttributeValue === 'no') {
        (elem as HTMLIFrameElement).scrolling = 'auto';
      }
    }
  });

  if (!autoSize) {
    return htmlDoc.body.innerHTML;
  }

  let resizeHeight = '100%';
  const resizeAbleElements = Array.from(htmlDoc.body.children).filter(elem =>
    autoSizeElementTypes.includes(elem.tagName.toLowerCase()),
  );
  if (resizeAbleElements.length > 1) {
    // If there are more than one resize-able element, we'll set the height to
    // 50%, to show multiple elements on the page
    resizeHeight = '50%';
  }
  Array.from(htmlDoc.body.children).forEach(elem => {
    const elemTagName = elem.tagName.toLowerCase();
    if (!autoSizeElementTypes.includes(elemTagName)) {
      return;
    }
    //
    if (elemTagName === 'div') {
      (elem as HTMLDivElement).style.height = resizeHeight;
      (elem as HTMLDivElement).style.width = '100%';
    }
    if (elemTagName === 'iframe') {
      (elem as HTMLIFrameElement).height = resizeHeight;
      (elem as HTMLIFrameElement).width = '100%';
    }
  });

  return htmlDoc.body.innerHTML;
};

/**
 * Method to format the created channel using useChannelForm form
 * @param formData { membersType: is the selected channel of company or client, membersId: list of client ids}
 * @returns
 */
export const formatExtensionChannelMembers = (
  formData: InitialConnectAppFormValues,
) => {
  const extensionItemFields: Partial<ExtensionItemFields> = {};

  const clientMemberIDs: Record<string, boolean> = {};
  const { memberIDs, membersType } = formData.fields;
  if (!memberIDs) return extensionItemFields;

  if (membersType === 'company') {
    // a company
    extensionItemFields.companyID = memberIDs[0];
  } else {
    // non-company extension channels are built from a list of clientIDs of the same company
    memberIDs.forEach(clientID => {
      clientMemberIDs[clientID] = true;
    });
  }
  extensionItemFields.memberIds = clientMemberIDs;
  return extensionItemFields;
};

/**
 * Method to check is the newly created channel already existed or not
 * @param dashboardItems list of all dashboard items i.e. app channels
 * @param item new selected channel that we are checking for
 * @returns
 */
export const findExistingDashboardItem = (
  dashboardItems: ExtensionItem[],
  item: Partial<ExtensionItem>,
) => {
  const dashboardMembersMap = item.fields?.memberIds || {};
  const newDashboardMemberIds = Object.keys(dashboardMembersMap);
  const existingChannel = dashboardItems.find(extensionChannelItem => {
    // check extension configId matches
    const extensionConfigIdMatches =
      extensionChannelItem.fields.extensionConfigId ===
      item.fields?.extensionConfigId;
    const extItemCompanyID = extensionChannelItem.fields.companyID;
    if (extItemCompanyID) {
      // if the new extension channel has a companyID matches with an existing channel with companyID we return that existing channel otherwise skip
      return (
        extensionConfigIdMatches && extItemCompanyID === item.fields?.companyID
      );
    }
    // if dashboard extension matches item and channelMembers in current
    const existingDashboardMemberIds = Object.keys(
      extensionChannelItem.fields.memberIds || {},
    );
    return (
      extensionConfigIdMatches &&
      // check channel members in extension match item members
      existingDashboardMemberIds.length === newDashboardMemberIds.length &&
      existingDashboardMemberIds.every(
        dashboardMemberId => dashboardMembersMap[dashboardMemberId],
      )
    );
  });

  return existingChannel;
};
